import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Table from '../../../components/common/Table/Table'
import plus from 'styleguide/icons/ds2/plus-white.svg'
import Print from '../../../../public/assets/images/printer-outline.svg'
import exportIcon from '../../../../public/assets/images/download-outline.svg'
import { usePrint } from '../../../hooks/usePrint'
import { useProfileDisplay } from '../../../hooks/useProfileDisplay'
import { usePaginationTracking } from '../../../hooks/usePaginationTracking'
import { transformRequestsToTableRows, DEFAULT_REQUEST_COLUMNS } from '../../../utils/tabDataTransform.tsx'
import {
  getAllRequests,
  setVacationBiddingPage,
  setVacationBiddingSortOrder,
} from '../../../store/slices/Requestor/employeeInfoSlice'
import { exportRequestorRequests } from '../../../store/slices/Requestor/requestorExportSlice'
import { RootState, AppDispatch } from '../../../store/store'
import Loader from '../../../components/common/Loader'
import ValidationBanner from '../../../components/common/ValidationBanner'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import { clearSelectedEmployee } from '../../../store/slices/admin/manualTimeOffSlice.ts'
import { clearEmployeeScopedData } from '../../../store/slices/Requestor/employeeInfoSlice.ts'

const VacationBidding: React.FC<{
  updateDataCount?: (tabId: number, count: number) => void
}> = ({ updateDataCount }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { printRef, handlePrint } = usePrint({ fontSize: '9px' })
  const { userInfo, userNuid, primaryApproverInfo } = useProfileDisplay()
const { mode } = useParams<{ mode?: string }>()
  // Determine mode from URL parameter
  const isAdminMode = mode === 'admin'
  const manualEmployee = useSelector((state: RootState) => state.manualTimeOff.selectedEmployee)
  const employeeInfoData = useSelector((state: RootState) => state.employeeInfo.employeeInfo.data)
  const printDisplayName = isAdminMode
    ? `${manualEmployee?.name || 'Employee'} (${manualEmployee?.nuid || 'Unknown'})`
    : `${userInfo}${userNuid}`
  const printDisplayApprover = isAdminMode
    ? (employeeInfoData?.primaryApprover || 'NA')
    : primaryApproverInfo
  const { requests, currentPage, pageSize, totalRecords, loading, error, sortOrder } =
    useSelector((state: RootState) => state.employeeInfo.vacationBidding)
const exportLoading = useSelector((state: any) => state.requestorExport.loading)
  const [activeSortColumn, setActiveSortColumn] = React.useState<string>('dateRequestedSortValue')
  const now = new Date()

  // Print state management
  const [printRows, setPrintRows] = React.useState<any[]>([])
  const [isPrinting, setIsPrinting] = React.useState(false)

  const getApiSortColumn = React.useCallback(
    (columnKey: string): 'fromDate' | 'hoursRequested' =>
      columnKey === 'hoursRequested' ? 'hoursRequested' : 'fromDate',
    []
  )

  const currentApiSortColumn = React.useMemo(
    () => getApiSortColumn(activeSortColumn),
    [activeSortColumn, getApiSortColumn]
  )

  // Track pagination changes to prevent redundant API calls
  const { pageChanged, pageSizeChanged, updateRefs } = usePaginationTracking(currentPage, pageSize)

  // Only fetch when pagination actually changes (not on component remount/tab switch)
  React.useEffect(() => {
    // Skip on initial mount - parent hook handles initial load
    // Only fetch if page or pageSize actually changed
    if (!pageChanged && !pageSizeChanged) {
      return
    }

    updateRefs()

    dispatch(
      getAllRequests({
        type: 'VACATION_BID',
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        day: now.getDate(),
        page: currentPage,
        perPage: pageSize,
        sortOrder,
        sortColumn: currentApiSortColumn,
      })
    )
  }, [dispatch, currentPage, pageSize, pageChanged, pageSizeChanged, updateRefs, sortOrder, currentApiSortColumn])

  React.useEffect(() => {
    if (updateDataCount) {
      updateDataCount(3, totalRecords)
    }
  }, [totalRecords, updateDataCount])

const handleCreateVacationBid = () => {
  const basePath = mode ? `/${mode}/requestor` : '/requestor'
  navigate(`${basePath}/create-vacation-bid`)
}
  const handleSelectEmployee = () => {
    // Clear redux + sessionStorage — the logged-in proxy's own profile was
    // never touched, so there is nothing to restore here.
    dispatch(clearSelectedEmployee())
    // Also clear the previously-fetched employee-scoped data itself (not
    // just the selection pointer) so nothing can render stale data for the
    // employee being exited while the next fetch is still in flight.
    dispatch(clearEmployeeScopedData())

    // Navigate to manual timeoff landing
    navigate('/admin/manual-timeoff')
  }
  const handlePageChange = (page: number) => {
    dispatch(setVacationBiddingPage(page))
  }

  const rows = transformRequestsToTableRows(requests)

  // Handle sorting by calling API with new sortOrder and sortColumn
  const handleSort = (columnKey: string, direction: 'ASC' | 'DESC') => {
    setActiveSortColumn(columnKey)
    dispatch(setVacationBiddingSortOrder(direction))
    const apiSortColumn = getApiSortColumn(columnKey)
    dispatch(
      getAllRequests({
        type: 'VACATION_BID',
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        day: now.getDate(),
        page: 1,
        perPage: pageSize,
        sortOrder: direction,
        sortColumn: apiSortColumn,
      })
    )
  }

  const handleExport = () => {
    const now = new Date()
  
    dispatch(
      exportRequestorRequests({
        date: now.toISOString().split('T')[0],
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        type: 'VACATION_BID',
        sortOrder,
        sortColumn: currentApiSortColumn,
      })
    )
  }

  /**
   * Fetch all records for printing without mutating Redux list pagination state.
   * Uses totalRecords directly for perPage, matching approver pattern.
   */
  const handlePrintWithApi = async () => {
    try {
      setIsPrinting(true)

      const now = new Date()
      const response = await api.get(
        endpoints.requester.getAllTimeOffReqList({
          type: 'VACATION_BID',
          year: now.getFullYear(),
          month: now.getMonth() + 1,
          day: now.getDate(),
          page: 1,
          perPage: totalRecords,
          sortOrder,
          sortColumn: currentApiSortColumn,
        })
      )

      const items = response.data?.items
      setPrintRows(Array.isArray(items) ? items : [])

      // Wait for DOM update before opening print dialog
      setTimeout(() => {
        handlePrint()
        setIsPrinting(false)
      }, 0)
    } catch {
      setIsPrinting(false)
    }
  }
  return (
    <div>
      <style>
        {`
#temp-print-container {
  display: none;
}

@media print {
  @page {
    size: landscape;
    margin: 10mm;
  }

  /* Show print table */
  #temp-print-container {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  #temp-print-container table {
    width: 100% !important;
    border-collapse: collapse !important;
  }

  #temp-print-container th,
  #temp-print-container td {
    border: 1px solid #ddd !important;
    padding: 8px !important;
    text-align: left !important;
  }

  #temp-print-container th {
    background-color: #f5f5f5 !important;
    font-weight: bold !important;
  }
}
        `}
      </style>
        {(exportLoading || isPrinting) && <Loader />}
      <div className="mt-2 mb-4 w-full">
        <div className="flex justify-end items-baseline gap-2">
          <button
            type="button"
            className="button -secondary flex items-center" 
            onClick={handlePrintWithApi}
            disabled={isPrinting}
          >
            <img src={Print} alt="Print Icon" className="inline-block mr-2" />
            <span>Print</span>
          </button>
          <button
            type="button"
            className="button -secondary flex items-center w-full md:w-auto"
            onClick={handleExport}
            title="Export File"
          >
        <img src={exportIcon} alt="Export Icon" className="inline-block mr-2" />
        <span className="text-sm leading-5">Export File</span>
      </button>
        </div>
        <h2 className="h2 mt-2">Vacation Bidding</h2>
      </div>
      <p className="p3 -medium">
        Please only utilize this vacation bidding tab when your department manager
        has notified you to begin submitting vacation bidding requests through
        TORT. TORT does not change your current contractual provisions or
        policies in place. Thank you.
      </p>

      <div className="mt-6! flex flex-col  w-fit">
        <button
          type="button"
          className="button -light flex items-center"
          data-analytics-type="button"
          onClick={handleCreateVacationBid}
        >
          <img src={plus} alt="Plus Icon" className="inline-block" />
          <span className="ml-2">{isAdminMode?"Create New Vacation Bid Request for this Employee":"Create a New Vacation Bid"}</span>
        </button>
         {isAdminMode &&
          <button
            type="button"
            className="button -light max-w-none! mt-8!"
            data-analytics-type="button"
            data-analytics-click="Default"
            onClick={handleSelectEmployee}
          >
            Select Another Employee
          </button>
          }
      </div>

      <div className="mt-4">
        {loading && <Loader />}
        {error && <ValidationBanner variant="error" heading={error} />}
        {!loading && !error && rows.length === 0 && (
          <div className="flex items-center justify-center min-h-96 py-12">
            <div className="text-center">
              <p className="p3 -medium">There are no current Vacation Bidding Requests.</p>
            </div>
          </div>
        )}
        {!loading && !error && rows.length > 0 && (
          <Table
            columns={DEFAULT_REQUEST_COLUMNS}
            rows={rows as any[]}
            showPagination={true}
            paginationProps={{
              totalItems: totalRecords,
              itemsPerPage: pageSize,
            }}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSort={handleSort}
            sortColumn={activeSortColumn}
            sortDirection={sortOrder}
          />
        )}
      </div>
      {printRows.length > 0 && (
        <div id="temp-print-container" ref={printRef}>
          <div style={{marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid #ccc'}}>
            <p style={{margin: '0 0 4px 0', fontWeight: 'bold', fontSize: '14px'}}>{printDisplayName}</p>
            <p style={{margin: '0 0 4px 0', fontSize: '12px'}}>Primary Approver: {printDisplayApprover}</p>
            <p style={{margin: '0', fontSize: '12px'}}>Vacation Bidding</p>
          </div>
          <Table
            columns={DEFAULT_REQUEST_COLUMNS}
            rows={transformRequestsToTableRows(printRows)}
            isSorting={false}
            showPagination={false}
          />
        </div>
      )}
    </div>
  )
}

export default VacationBidding
